import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout from "@/pages/ArticleLayout";

const CoZahrnujeUklidSpolecnychProstorBytovehoDomu = () => (
  <ArticleLayout
    title="Co zahrnuje úklid společných prostor bytového domu?"
    metaTitle="Co zahrnuje úklid společných prostor bytového domu? Kompletní přehled"
    description="Co patří do pravidelného úklidu bytového domu — schodiště, výtah, vchod, suterén, okolí budovy. Denní, týdenní a měsíční úkoly a co bývá zvláštní položkou."
    path="/rady-pro-spravu-uklidu/co-zahrnuje-uklid-spolecnych-prostor-bytoveho-domu"
    publishedAt="2026-07-08"
    readingTime="6 min"
  >
    <p>
      Pravidelný úklid bytového domu zahrnuje víc než jen zametení
      schodiště. Pro SVJ a správce budov je důležité vědět, co je
      standardní součástí smlouvy a co se hradí zvlášť. Tady je
      přehled.
    </p>

    <h2>Standardní rozsah pravidelného úklidu</h2>

    <p>
      Standardní <Link to="/uklid-bytovych-domu-brno">úklid společných
      prostor</Link> obvykle zahrnuje úklid a vytírání schodišť a
      podest ve všech patrech, čištění zábradlí a madel, úklid
      vstupního vestibulu a vchodových dveří, vynášení odpadků ze
      společných prostor a kontrolu koše, čištění poštovních schránek
      a nástěnek a kontrolu pořádku v suterénu a sklepních chodbách.
    </p>

    <p>
      Rozsah se dá rozšířit i zúžit podle potřeb konkrétního domu.
      Klíčové je mít ho jasně nadefinovaný ve smlouvě, aby obě strany
      věděly, co je součástí paušálu a co se řeší jako doplňková
      služba.
    </p>

    <h2>Výtah — zvláštní položka nebo součást paušálu?</h2>

    <p>
      Úklid kabiny výtahu (podlaha, stěny, zrcadla, tlačítka) bývá
      obvykle zvláštní položkou nebo součástí rozšířeného balíčku.
      Frekvence je typicky 1–2× týdně podle provozu domu — u domů
      s vysokou návštěvností a živým parterem je vyšší frekvence
      logická, u klidnějších domů stačí jednou týdně.
    </p>

    <p>
      Ve SANVIORO řešíme úklid výtahu jako součást pravidelné
      smlouvy, pokud se to dohodne dopředu při návrhu rozsahu. Není
      to nadstandard, jen samostatná položka, aby cena byla
      transparentní a klient přesně věděl, za co platí.
    </p>

    <h2>Okolí budovy a venkovní prostory</h2>

    <p>
      Zametení prostoru před vchodem, úklid stání pro kola a údržba
      místa kolem popelnic jsou často součástí rozšířených smluv.
      U menších domů obvykle stačí týdenní frekvence, u větších
      komplexů s vyšší návštěvností dvakrát týdně.
    </p>

    <p>
      Sezónní úkoly — odklízení sněhu v zimě a shrabávání listí na
      podzim — se obvykle řeší samostatnou dohodou. Důvod je
      jednoduchý: rozsah těchto prací je velmi kolísavý podle počasí,
      takže paušál by pro obě strany byl nespravedlivý. Praxe je
      dohodnout hodinovou sazbu nebo cenu za výjezd.
    </p>

    <h2>Frekvence úklidu — co doporučujeme?</h2>

    <p>
      Optimální frekvenci určuje typ domu, návštěvnost a preference
      SVJ. Následující tabulka ukazuje doporučené hodnoty, které se
      v Brně osvědčily u většiny bytových domů.
    </p>

    <div
      className="my-6 overflow-x-auto border border-[#E5E5E5] rounded-lg"
      data-testid="frequency-table-wrapper"
    >
      <table className="w-full text-[15px] text-left border-collapse">
        <thead className="bg-[#F5F5F7] text-[#111111]">
          <tr>
            <th className="p-4 font-semibold border-b border-[#E5E5E5]">
              Prostor
            </th>
            <th className="p-4 font-semibold border-b border-[#E5E5E5] whitespace-nowrap">
              Doporučená frekvence
            </th>
            <th className="p-4 font-semibold border-b border-[#E5E5E5]">
              Poznámka
            </th>
          </tr>
        </thead>
        <tbody className="text-[#222222]">
          <tr className="border-b border-[#E5E5E5]">
            <td className="p-4">Schodiště a podesty</td>
            <td className="p-4 font-semibold text-[#0F5C69] whitespace-nowrap">
              1–2× týdně
            </td>
            <td className="p-4">podle provozu domu</td>
          </tr>
          <tr className="border-b border-[#E5E5E5]">
            <td className="p-4">Vstupní hala a vchod</td>
            <td className="p-4 font-semibold text-[#0F5C69] whitespace-nowrap">
              2–3× týdně
            </td>
            <td className="p-4">první dojem pro návštěvníky</td>
          </tr>
          <tr className="border-b border-[#E5E5E5]">
            <td className="p-4">Výtah</td>
            <td className="p-4 font-semibold text-[#0F5C69] whitespace-nowrap">
              1–2× týdně
            </td>
            <td className="p-4">zvláštní položka</td>
          </tr>
          <tr className="border-b border-[#E5E5E5]">
            <td className="p-4">Suterén a chodby</td>
            <td className="p-4 font-semibold text-[#0F5C69] whitespace-nowrap">
              1× měsíčně
            </td>
            <td className="p-4">nebo dle potřeby</td>
          </tr>
          <tr>
            <td className="p-4">Okolí budovy</td>
            <td className="p-4 font-semibold text-[#0F5C69] whitespace-nowrap">
              1× týdně
            </td>
            <td className="p-4">sezónně</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h2>Co není součástí standardního úklidu?</h2>

    <p>
      Standardní paušál obvykle neobsahuje mytí oken (objednává se
      zvlášť, typicky 2× ročně), generální úklid sklepů (jednorázová
      služba), dezinfekci po havárii (voda, kanalizace) a úklid po
      rekonstrukci. Tyto úkony se objednávají jako jednorázové
      služby s vlastní cenovou nabídkou, obvykle po prohlídce a
      určení rozsahu.
    </p>

    <p>
      Pokud potřebujete kombinaci pravidelného úklidu i občasných
      generálů, doporučujeme mít vše u jednoho dodavatele. Tým už
      dům zná, ceny bývají výhodnější a koordinace hladká. Další
      tipy k organizaci úklidu najdete v naší sekci{" "}
      <Link to="/rady-pro-spravu-uklidu">rad pro správu úklidu</Link>.
    </p>

    <h2>Chcete vědět, co přesně zahrneme do vaší smlouvy?</h2>

    <p>
      Přijedeme na prohlídku, posoudíme rozsah a připravíme písemný
      návrh do 24 hodin. Smlouva jasně stanoví, co je a co není
      součástí — žádná překvapení. Rádi na místě rovnou navrhneme
      optimální frekvenci pro váš konkrétní dům podle typu provozu
      a rozpočtu SVJ nebo správce.
    </p>

    <p className="mt-10">
      <Link
        to="/#calc"
        data-testid="article-cta"
        className="inline-flex items-center gap-3 bg-[#0F5C69] hover:bg-[#0B4B55] text-white no-underline decoration-transparent px-7 h-14 font-semibold tracking-tight transition-colors"
      >
        Získat nezávaznou nabídku
      </Link>
    </p>
  </ArticleLayout>
);

export default CoZahrnujeUklidSpolecnychProstorBytovehoDomu;
